/**
 * Fill in the invoice-setting flags that default to ON.
 *
 * An invoice snapshots its settings at creation time, and createConfigSnapshot
 * stores `null` when the society has no invoice_settings row yet. The templates
 * then read `config = invoice.configSnapshot?.invoice_setting_snapshot || {}`
 * and test flags with `=== 1`, so every one of them was false and whole
 * sections silently vanished — arrears and interest most visibly, because
 * arrear_display gates them and its column default is 1, not 0.
 *
 * Only flags whose column default is 1 are filled in; anything defaulting to 0
 * (every hide_* flag, show_total_*, and the rest) stays off when unset, which
 * is already what the `=== 1` tests do.
 *
 * Keep this list in step with the tinyint columns on invoice_settings that
 * declare DEFAULT 1.
 */
const DEFAULT_ON_FLAGS = [
    'show_flat_area',
    'arrear_display',
    'show_invoice_note',
    'show_receipt_note',
    'amount_editable',
];

/**
 * Settings to print an invoice with: its creation-time snapshot, overlaid with
 * the CURRENT print / display settings of its billing type (the API sends those
 * as invoice.current_print_settings - template, show / hide switches, notes).
 * Hiding a detail in Functional Settings used to never reach invoices created
 * before the change. Billing figures in the snapshot are left as they were.
 */
export const printConfigFor = (invoice) => {
    const snapshot = invoice?.configSnapshot?.invoice_setting_snapshot || {};
    const live = invoice?.current_print_settings || {};
    const current = {};
    Object.entries(live).forEach(([k, v]) => { if (v !== undefined) current[k] = v; });
    return withSettingDefaults({ ...snapshot, ...current });
};

export const withSettingDefaults = (config) => {
    const resolved = { ...(config || {}) };
    for (const flag of DEFAULT_ON_FLAGS) {
        if (resolved[flag] === undefined || resolved[flag] === null) {
            resolved[flag] = 1;
        }
    }
    return resolved;
};
